import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-(--radius-sm) font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-(--color-ring) focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-(--color-cta-primary) text-(--color-cta-primary-foreground) border border-(--color-cta-primary-border) shadow hover:bg-(--color-cta-primary-hover)",
				destructive:
					"bg-(--color-error-500) text-(--color-neutral-0) border border-(--color-neutral-0)/20 shadow-sm hover:bg-(--color-error-600) hover:border-(--color-neutral-0)/30 [--color-ring:var(--color-error-300)]",
				outline:
					"border border-(--color-border) bg-(--color-background) text-(--color-foreground) shadow-sm hover:bg-(--color-muted) hover:border-(--color-accent-border)",
				secondary:
					"bg-(--color-muted) text-(--color-foreground) border border-(--color-border-default) shadow-sm hover:border-(--color-border-strong)",
				subtle:
					"bg-(--color-muted) text-(--color-foreground) shadow-sm hover:bg-(--color-neutral-200) dark:hover:bg-(--color-neutral-700)",
				ghost: "hover:bg-(--color-muted) hover:text-(--color-foreground)",
				link: "text-(--color-primary) underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 rounded-(--radius-xs) px-3 text-xs",
				lg: "h-10 rounded-(--radius-sm) px-8",
				icon: "h-9 w-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
